// Shared expiration logic for the date-range condition, used by both the
// Manage Time drawer and the policy-card condition cell so the "expired" /
// "expiring" thresholds stay in sync.

export type DateRangeStatus = "normal" | "expiring" | "expired";
export type DateRangeMode = "absolute" | "relative";
export type RelativeUnit = "days" | "weeks" | "months";

export interface WeekDayOption {
  label: string;
  value: string;
}

// Canonical shape of a single datetime rule condition, shared by the Manage
// Time drawer and the policy-card cell.
export interface DateTimeRuleValue {
  // 24-hour clock. startTime 0 = 00:00; endTime 0 = end of day.
  startTime: number;
  endTime: number;
  startMinutes?: string;
  endMinutes?: string;
  allDay?: boolean;
  weekdays?: WeekDayOption[];
  dateRangeEnabled?: boolean;
  dateRangeMode?: DateRangeMode;
  dateRangeStart?: string;
  dateRangeEnd?: string;
  dateRangeRelativeAmount?: number;
  dateRangeRelativeUnit?: RelativeUnit;
  timezone?: string;
}

// Only the date-range fields are needed for status; accepts the full value or
// any partial carrying these fields.
export type DateRangeFields = Pick<
  DateTimeRuleValue,
  | "dateRangeEnabled"
  | "dateRangeMode"
  | "dateRangeStart"
  | "dateRangeEnd"
  | "dateRangeRelativeAmount"
  | "dateRangeRelativeUnit"
>;

// Default window (in days) before the end date during which the rule counts as
// "expiring" (warning state).
export const EXPIRING_SOON_DAYS = 7;

// Max date for the condition: backend accepts a 4-digit year only. Single
// source of truth (also imported by the Internet Access MFE).
export const MAX_SELECTABLE_DATE = new Date(9999, 11, 31);

export const startOfDay = (date: Date): Date => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

// Absolute dates are stored as local YYYY-MM-DD (see toISODate in ManageTime).
// Parse them as local midnight so a UTC parse never shifts the day.
export const parseLocalDate = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

// The effective end date of the rule, or null when no date range applies.
// Relative ranges are resolved against "now" (today + amount/unit), matching
// how the payload is serialized on save.
export const getEffectiveEndDate = (value: DateRangeFields): Date | null => {
  if (!value.dateRangeEnabled) return null;

  if (value.dateRangeMode === "relative") {
    // The form stores "" while the amount field is being cleared, so `?? 1`
    // isn't enough — coerce and fall back on any non-positive/NaN value.
    const amount = Number(value.dateRangeRelativeAmount) || 1;
    const unit = value.dateRangeRelativeUnit ?? "days";
    const end = new Date();
    if (unit === "days") end.setDate(end.getDate() + amount);
    else if (unit === "weeks") end.setDate(end.getDate() + amount * 7);
    else end.setMonth(end.getMonth() + amount);
    return end;
  }

  return value.dateRangeEnd ? parseLocalDate(value.dateRangeEnd) : null;
};

export const getDateRangeStatus = (
  value: DateRangeFields,
  expiringSoonDays: number = EXPIRING_SOON_DAYS
): DateRangeStatus => {
  const end = getEffectiveEndDate(value);
  if (!end) return "normal";

  const today = startOfDay(new Date());
  const endDay = startOfDay(end);

  // The rule is active through the end date, so it's expired only once the end
  // date is strictly in the past.
  if (endDay < today) return "expired";

  const threshold = new Date(today);
  threshold.setDate(threshold.getDate() + expiringSoonDays);
  return endDay <= threshold ? "expiring" : "normal";
};
